/**
 * External dependencies
 */
import 'toolcool-range-slider';

/**
 * PodcastPlayer
 *
 * Encapsulates all state and behaviour for the CAI podcast widget.
 */
class PodcastPlayer {

	/* ─── Constants ─────────────────────────────────────────────── */

	static SPEEDS      = [ 1, 1.25, 1.5, 1.75, 2 ];
	static VOLUME_HALF   = 0.5;
	static SKIP_BACK_SEC = -10;
	static SKIP_FWD_SEC  =  30;

	/* ─── Constructor ───────────────────────────────────────────── */

	constructor() {
		this._speedIdx = 0;
		this._els      = this.#queryElements();

		this.#bindPlayerEvents();
		this.#bindControlEvents();
	}

	/* ─── Private – DOM helpers ─────────────────────────────────── */

	/**
	 * Cache every DOM element the player needs.
	 *
	 * @returns {Object} Map of element references.
	 */
	#queryElements() {
		const q = ( id ) => document.getElementById( id );
		return {
			player       : q( 'cai-podcast-player' ),
			widget       : q( 'cai-podcast-widget' ),
			play         : q( 'cai-podcast-play' ),
			back         : q( 'cai-podcast-back' ),
			forward      : q( 'cai-podcast-forward' ),
			speed        : q( 'cai-podcast-speed' ),
			volume       : q( 'cai-podcast-volume' ),
			scrubber     : q( 'cai-podcast-scrubber' ),
			progressFill : q( 'cai-podcast-progress-fill' ),
			currentTime  : q( 'cai-podcast-current-time' ),
			totalTime    : q( 'cai-podcast-total-time' ),
			volumeSlider : q( 'cai-podcast-volume-slider' ),
		};
	}

	/* ─── Private – Event binding ───────────────────────────────── */

	/**
	 * Attach listeners to the underlying <audio> element.
	 */
	#bindPlayerEvents() {
		const { player, scrubber, volumeSlider } = this._els;

		if ( ! player ) {
			return;
		}

		player.addEventListener( 'timeupdate',    () => this.#onTimeUpdate() );
		player.addEventListener( 'loadedmetadata', () => this.#onMetadataLoaded() );
		player.addEventListener( 'ended',          () => this.#setPlaying( false ) );

		// Buffering — fires when the browser stalls waiting for data (e.g. after a seek).
		player.addEventListener( 'waiting',  () => this.#setBuffering( true ) );
		player.addEventListener( 'seeking',  () => this.#setBuffering( true ) );

		// Resume — fires once enough data has loaded to continue playback.
		player.addEventListener( 'canplay',  () => this.#setBuffering( false ) );
		player.addEventListener( 'playing',  () => this.#setBuffering( false ) );
		player.addEventListener( 'seeked',   () => this.#setBuffering( false ) );

		scrubber.addEventListener( 'input', () => this.#onScrub() );
		volumeSlider.addEventListener( 'change', ( evt ) => this.#onVolumeChange( evt ) );
	}

	/**
	 * Attach listeners to the UI controls.
	 */
	#bindControlEvents() {
		const { play, back, forward, speed, volume } = this._els;

		play?.addEventListener(    'click', () => this.togglePlay() );
		back?.addEventListener(    'click', () => this.skipBy( PodcastPlayer.SKIP_BACK_SEC ) );
		forward?.addEventListener( 'click', () => this.skipBy( PodcastPlayer.SKIP_FWD_SEC ) );
		speed?.addEventListener(   'click', () => this.cycleSpeed() );
		volume?.addEventListener(  'click', () => this.toggleMute() );
	}

	/* ─── Private – Player event handlers ──────────────────────── */

	/**
	 * Sync the scrubber and elapsed-time label on every time tick.
	 */
	#onTimeUpdate() {
		const { player, progressFill, scrubber, currentTime } = this._els;
		if ( !player.duration ) return;

		const pct = ( player.currentTime / player.duration ) * 100;
		progressFill.style.width        = pct + '%';
		scrubber.value                  = pct;
		currentTime.textContent         = PodcastPlayer.#fmtTime( player.currentTime );
	}

	/**
	 * Populate the total-duration label once metadata is available.
	 */
	#onMetadataLoaded() {
		this._els.totalTime.textContent = PodcastPlayer.#fmtTime( this._els.player.duration );
	}

	/**
	 * Seek the audio to the scrubber's current position.
	 */
	#onScrub() {
		const { player, scrubber } = this._els;
		if ( player.duration )
			player.currentTime = ( scrubber.value / 100 ) * player.duration;
	}

	/**
	 * Apply the new volume from the range-slider custom event.
	 *
	 * @param {CustomEvent} evt
	 */
	#onVolumeChange( evt ) {
		const { player } = this._els;
		player.muted  = false;
		player.volume = evt.detail.value;
		this.#setVolumeIcon( evt.detail.value );
	}

	/* ─── Private – UI state helpers ───────────────────────────── */

	/**
	 * Toggle the `is-playing` class on the widget root.
	 *
	 * @param {boolean} playing
	 */
	#setPlaying( playing ) {
		this._els.widget?.classList.toggle( 'is-playing', playing );
	}

	/**
	 * Update the volume button icon classes based on current level.
	 *
	 * @param {number} v  Volume level in [0, 1].
	 */
	#setVolumeIcon( v ) {
		const { volume } = this._els;
		volume.classList.toggle( 'mute', v === 0 );
		volume.classList.toggle( 'half', v > 0 && v < PodcastPlayer.VOLUME_HALF );
	}

	/**
	 * Show or hide the buffering spinner.
	 *
	 * Adds `is-buffering` to the widget root (so the play button can also be
	 * hidden/replaced via CSS) and toggles the spinner element's visibility.
	 *
	 * @param {boolean} buffering
	 */
	#setBuffering( buffering ) {
		const { widget } = this._els;
		widget?.classList.toggle( 'is-buffering', buffering );
	}

	/* ─── Private – Static utilities ───────────────────────────── */

	/**
	 * Convert seconds to a human-readable time string (H:MM:SS or M:SS).
	 *
	 * @param  {number} s  Seconds.
	 * @returns {string}
	 */
	static #fmtTime( s ) {
		if ( !s || isNaN( s ) ) return '0:00';

		const h   = Math.floor( s / 3600 );
		const m   = Math.floor( ( s % 3600 ) / 60 );
		const sec = Math.floor( s % 60 ).toString().padStart( 2, '0' );

		return h > 0
			? `${ h }:${ m.toString().padStart( 2, '0' ) }:${ sec }`
			: `${ m }:${ sec }`;
	}

	/* ─── Public API ────────────────────────────────────────────── */

	/**
	 * Toggle between play and pause.
	 */
	togglePlay() {
		const { player } = this._els;
		if ( player.paused ) {
			player.play();
			this.#setPlaying( true );
		} else {
			player.pause();
			this.#setPlaying( false );
		}
	}

	/**
	 * Jump forward or backward by the given number of seconds.
	 *
	 * @param {number} secs  Positive to skip forward, negative to rewind.
	 */
	skipBy( secs ) {
		const { player } = this._els;
		player.currentTime = Math.max( 0, Math.min( player.duration || 0, player.currentTime + secs ) );
	}

	/**
	 * Advance to the next playback speed in the cycle.
	 */
	cycleSpeed() {
		this._speedIdx         = ( this._speedIdx + 1 ) % PodcastPlayer.SPEEDS.length;
		const rate             = PodcastPlayer.SPEEDS[ this._speedIdx ];
		this._els.player.playbackRate  = rate;
		this._els.speed.textContent    = rate + '×';
	}

	/**
	 * Toggle the muted state and refresh the volume icon.
	 */
	toggleMute() {
		const { player } = this._els;
		player.muted = !player.muted;
		this.#setVolumeIcon( player.muted ? 0 : player.volume );
	}
}

/* ─── Boot ──────────────────────────────────────────────────────── */
new PodcastPlayer();
